import { clearError as clearOpError } from '@gw/ui';

import { writeSpecField } from '@/lib/openapi/spec-fields';

import type { ErrorStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface DatasetWizardSlice {
  // The draft spec -- this IS the domain state
  wizardDraftSpec: Record<string, unknown> | null;

  // Source tracking (not in spec)
  wizardSpecSource: { type: 'url' | 'upload' | 'inline'; url?: string };

  // Credential secrets (parallel channel, never in spec)
  wizardCredentialSecrets: {
    username?: string;
    password?: string;
    token?: string;
    keyValue?: string;
    clientId?: string;
    clientSecret?: string;
    tokenUrl?: string;
  };
  wizardCredentialsChanged: boolean;

  // Icon upload (parallel channel, uploaded after create/during edit)
  wizardIconFile: File | null;
  wizardIconPreview: string | null;

  // Submission state
  wizardIsSubmitting: boolean;
  wizardErrors: ErrorStates;

  // Actions
  initDraft: (spec: Record<string, unknown>) => void;
  updateDraftFields: (updates: Array<{ path: string; value: unknown }>) => void;
  updateDraftExtension: (extensionName: string, value: unknown) => void;
  updateDraftField: (path: string, value: unknown) => void;
  getDraftSpec: () => Record<string, unknown> | null;
  setWizardCredentialSecrets: (
    secrets: Partial<DatasetWizardSlice['wizardCredentialSecrets']>
  ) => void;
  setWizardIconFile: (file: File | null) => void;
  setWizardIconPreview: (url: string | null) => void;
  setWizardIsSubmitting: (isSubmitting: boolean) => void;
  setWizardSpecSource: (source: DatasetWizardSlice['wizardSpecSource']) => void;
  resetWizard: () => void;
  clearWizardError: (operation?: string) => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialWizardState: Omit<
  DatasetWizardSlice,
  | 'initDraft'
  | 'updateDraftFields'
  | 'updateDraftExtension'
  | 'updateDraftField'
  | 'getDraftSpec'
  | 'setWizardCredentialSecrets'
  | 'setWizardIconFile'
  | 'setWizardIconPreview'
  | 'setWizardIsSubmitting'
  | 'setWizardSpecSource'
  | 'resetWizard'
  | 'clearWizardError'
> = {
  wizardDraftSpec: null,
  wizardSpecSource: { type: 'url' },
  wizardCredentialSecrets: {},
  wizardCredentialsChanged: false,
  wizardIconFile: null,
  wizardIconPreview: null,
  wizardIsSubmitting: false,
  wizardErrors: {},
};

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createDatasetWizardSlice: StateCreator<
  DatasetWizardSlice,
  [],
  [],
  DatasetWizardSlice
> = (set, get) => ({
  ...initialWizardState,

  initDraft: (spec) => {
    // Deep-clone the spec to avoid external mutation
    const cloned = structuredClone(spec);
    set({ wizardDraftSpec: cloned });
  },

  updateDraftFields: (updates) => {
    const currentSpec = get().wizardDraftSpec;
    if (!currentSpec) return;

    let result = currentSpec;
    for (const { path, value } of updates) {
      result = writeSpecField(result, path, value);
    }

    set({ wizardDraftSpec: result });
  },

  updateDraftExtension: (extensionName, value) => {
    const currentSpec = get().wizardDraftSpec;
    if (!currentSpec) return;

    const key = extensionName.startsWith('x-gw-') ? extensionName : `x-gw-${extensionName}`;
    set({
      wizardDraftSpec: { ...currentSpec, [key]: value },
    });
  },

  updateDraftField: (path, value) => {
    const currentSpec = get().wizardDraftSpec;
    if (!currentSpec) return;

    set({ wizardDraftSpec: writeSpecField(currentSpec, path, value) });
  },

  getDraftSpec: () => {
    return get().wizardDraftSpec;
  },

  setWizardCredentialSecrets: (secrets) => {
    const currentSecrets = get().wizardCredentialSecrets;
    set({
      wizardCredentialSecrets: { ...currentSecrets, ...secrets },
      wizardCredentialsChanged: true,
    });
  },

  setWizardIconFile: (file) => {
    set({ wizardIconFile: file });
  },

  setWizardIconPreview: (url) => {
    set({ wizardIconPreview: url });
  },

  setWizardIsSubmitting: (isSubmitting) => {
    set({ wizardIsSubmitting: isSubmitting });
  },

  setWizardSpecSource: (source) => {
    set({ wizardSpecSource: source });
  },

  resetWizard: () => {
    set({ ...initialWizardState });
  },

  clearWizardError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ wizardErrors: clearOpError(currentState.wizardErrors, operation) });
    } else {
      set({ wizardErrors: {} });
    }
  },
});
